import { useState, useEffect, useImperativeHandle, forwardRef, useRef, useMemo, ChangeEvent } from 'react';
import { AccountType, Checkbox, RadioButton, Theme, useTranslation } from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import { type AppState, type AppDispatch } from '@nextgen-web-framework/core';
import {
  DynamicObjectInterface,
  profileApi,
  SponsorSearchResponseInner,
  updateRegistrationData,
} from '@commerce-profile-webcomponents/sdk';
import { FloatErrorMessage } from '../Common/FloatErrorMessage/FloatErrorMessage';
import { transformValidationArrayIntoObj } from '../../utils/registrationUtils';
import { useRegistrationFieldsMasterData } from '../../utils/masterdata';
import SelectSponsor from './SelectSponsor/SelectSponsor';
import styles from './RegistrationSponsor.module.scss';
import { DoNotKnowSponsorFlow, RegisterationSponsorRef, RegistrationSponsorProps } from './types/propTypes';
import LeadsSearch from './LeadsSearch/LeadsSearch';
import { SelectSponsorRef } from './SelectSponsor/types/propTypes';

const RegistrationSponsor = forwardRef<RegisterationSponsorRef, RegistrationSponsorProps>((props, ref) => {
  const { isFieldDisabled, showABOSelected, isFocused, wrapperClass, state, city, onLeadsSearchHandler } = props;

  const t = useTranslation();
  //slice
  const dispatch = useDispatch<AppDispatch>();
  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const { selectSponsor, perosonalDetails } = registrationFlowData;
  const { sponsor, editSponsor, hideSponsorRadioSelection } = selectSponsor;
  const sponsorExist = sponsor?.accountNumber;
  const [assignAbo, setAssignAbo] = useState(false);
  const [iKnowABO, setIKnowABO] = useState(false);
  const [iDontKnowABO, setIDontKnowABO] = useState(false);
  const [showloading, setShowloading] = useState(false);
  const [isLeadSearchDrawerOpen, setIsLeadSearchDrawerOpen] = useState<boolean>();
  const selectSponsorRef = useRef<SelectSponsorRef>(null);

  const [showGenericErrorMessage, setShowGenericErrorMessage] = useState<boolean>();
  const [showCheckBoxErrorMessage, setShowCheckBoxErrorMessage] = useState<boolean>();

  const registrationFieldsMasterData = useRegistrationFieldsMasterData();
  const registrationFieldsMasterDataPk = useMemo(
    () => transformValidationArrayIntoObj(registrationFieldsMasterData, 'pk'),
    [],
  );
  const dontKnowFlowTypeConfig = useMemo(() => {
    const sponsorValidations = registrationFieldsMasterDataPk?.['sponsor'];
    return (sponsorValidations as DynamicObjectInterface)?.validations?.[selectedAccountType]?.doNotKnowSponsorFlow;
  }, [selectedAccountType]);

  const showLeadsSearch =
    isLeadSearchDrawerOpen &&
    iDontKnowABO &&
    [DoNotKnowSponsorFlow.WITHOUT_POSTAL_CODE_SEARCH, DoNotKnowSponsorFlow.POSTAL_CODE_SEARCH].includes(
      dontKnowFlowTypeConfig,
    );

  const showAutomaticSponsorSearchCheckBox =
    iDontKnowABO &&
    [DoNotKnowSponsorFlow.RANDOM, DoNotKnowSponsorFlow.RANDOM_WITH_SPONSOR_INFO].includes(dontKnowFlowTypeConfig);

  const getRadioTheme = (radio: boolean) => (radio ? Theme.PURPLE : Theme.BLACK);

  const updateRandomLeadsAssignConsent = (val: boolean) => {
    dispatch(
      updateRegistrationData({
        perosonalDetails: {
          ...perosonalDetails,
          assignAboCheckbox: val,
        },
      }),
    );
  };

  useEffect(() => {
    updateRandomLeadsAssignConsent(assignAbo);
  }, [assignAbo]);

  useImperativeHandle(ref, () => ({
    validateSponsor: validateRegisterationSponsor,
    resetSponsorValidation: () => {
      setShowGenericErrorMessage(false);
      setShowCheckBoxErrorMessage(false);
      selectSponsorRef?.current?.resetSponsorValidation();
    },
  }));

  const validateRegisterationSponsor = () => {
    if (iKnowABO && !selectSponsorRef?.current?.selectSponsorErrorMsg) {
      selectSponsorRef?.current?.validateSponsor();
    }
    setShowGenericErrorMessage(!isFieldDisabled && !iDontKnowABO && !iKnowABO);
    setShowCheckBoxErrorMessage(!isFieldDisabled && showAutomaticSponsorSearchCheckBox && !assignAbo);
  };

  const handleKnownABO = () => {
    setIKnowABO(true);
    setIDontKnowABO(false);
    updateRandomLeadsAssignConsent(false);
    setShowCheckBoxErrorMessage(false);
    setIsLeadSearchDrawerOpen(false);
    setShowGenericErrorMessage(false);
  };
  const handleDontKnownABO = () => {
    setIKnowABO(false);
    setIDontKnowABO(true);
    setShowCheckBoxErrorMessage(false);
    setIsLeadSearchDrawerOpen(true);
    setShowGenericErrorMessage(false);
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          sponsor: {},
        },
      }),
    );
  };
  //sponsor component props
  const sponsorProps = {
    isIdVerified: isFieldDisabled,
    showInternationalSponsor: sponsorExist,
    showInsideModal: showABOSelected,
    isFocused,
    onLeadsSearchHandler,
  };

  const onSponsorCheckBoxChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAssignAbo((prev) => !prev);
    setShowCheckBoxErrorMessage(!e.target.checked);
    if (e.target.checked && dontKnowFlowTypeConfig === DoNotKnowSponsorFlow.RANDOM_WITH_SPONSOR_INFO) {
      setShowloading(true);
      profileApi
        .getLeadsListWithoutPostalCode({
          accountType: selectedAccountType,
        })
        .then((res: SponsorSearchResponseInner[]) => {
          setShowloading(false);
          if (res && res.length) {
            onSponsorSelect(res?.[0]);
          }
        })
        .catch(() => {
          setShowloading(false);
        });
    }
  };

  useEffect(() => {
    if (!editSponsor) {
      setIKnowABO(true);
    }
  }, [editSponsor]);
  useEffect(() => {
    if (showABOSelected) {
      setIKnowABO(true);
    }
  }, []);

  const onSponsorSelect = (abo: SponsorSearchResponseInner | null) => {
    if (abo) {
      dispatch(
        updateRegistrationData({
          selectSponsor: {
            ...selectSponsor,
            sponsor: abo,
            hideSponsorRadioSelection: true,
          },
        }),
      );
      onLeadsSearchHandler?.(true);
      handleKnownABO(); //move to I know ABO step since user selected from leads list.
    }
  };

  return (
    <div
      className={`${styles['sponsor-details']}
    ${!showABOSelected ? styles['padding-bottom'] : ''} ${wrapperClass}`}
      data-testid="sponsor-details"
    >
      {!hideSponsorRadioSelection && (
        <>
          <div className={`${styles['sponsor-radio']} ${iKnowABO || iDontKnowABO ? styles['sponsor-padding'] : ''}`}>
            <RadioButton
              id="i-know-an-abo"
              data-testid="i-know-an-abo"
              className={styles['radio-button']}
              label={t('profile.RegistrationPersonalDetailsStep.IknowAnAbo')}
              checked={iKnowABO}
              theme={getRadioTheme(iKnowABO)}
              onClick={handleKnownABO}
              disabled={isFieldDisabled}
            />
            <RadioButton
              id="i-dont-know-an-abo"
              data-testid="i-dont-know-an-abo"
              className={styles['radio-button']}
              label={t('profile.RegistrationPersonalDetailsStep.IDontknowAnAbo')}
              checked={iDontKnowABO}
              theme={getRadioTheme(iDontKnowABO)}
              onClick={handleDontKnownABO}
              disabled={isFieldDisabled}
            />
          </div>
          {showGenericErrorMessage && <FloatErrorMessage message={t('profile.validationMsg.sponsor.required')} />}
        </>
      )}
      <>
        {(iKnowABO || sponsorExist) && <SelectSponsor {...sponsorProps} ref={selectSponsorRef} />}

        {showAutomaticSponsorSearchCheckBox && !showloading && (
          <Checkbox
            data-testid="assign-abo-checkbox-testid"
            text={t('profile.RegistrationPersonalDetailsStep.IDontknowAnAbocheckbox')}
            className="assign-abo-checkbox"
            checked={perosonalDetails.assignAboCheckbox}
            onChange={onSponsorCheckBoxChange}
          />
        )}
        {dontKnowFlowTypeConfig == DoNotKnowSponsorFlow.RANDOM_WITH_SPONSOR_INFO && showloading && (
          <div className={styles['sponsor-details-checkbox-loader']}>
            <div className={styles['spinner']}></div>
            <div>{t('profile.RegistrationSponsor.loadingMsg')}</div>
          </div>
        )}

        {showCheckBoxErrorMessage && (
          <FloatErrorMessage message={t('profile.validationMsg.sponsorCheckbox.required')} />
        )}
        {showLeadsSearch && (
          <LeadsSearch
            isFieldDisabled={isFieldDisabled}
            state={state}
            city={city}
            callback={(abo: SponsorSearchResponseInner | null) => {
              onSponsorSelect(abo);
              setIsLeadSearchDrawerOpen(false);
            }}
            doNotKnowAboFlowType={dontKnowFlowTypeConfig}
          />
        )}
      </>
    </div>
  );
});

RegistrationSponsor.displayName = 'RegistrationSponsor';
export default RegistrationSponsor;
